import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { ApplicationSdk } from '../../../core/sdk';

interface Object_Simple {
    id: number;
    name: string;
}

/**
 * The dialog store for the manager
 */
class ManagerDialogStore {
    private sdk: ApplicationSdk;

    public manager_id: number | null = null;

    public manager_firstname: string = '';
    public manager_lastname: string = '';
    public manager_mail: string = '';
    public manager_assigned_objects: Set<number> = new Set();

    public originalData: any = null;
    public objectDataLoading: boolean = false;
    public managerDataLoading: boolean = false;

    public assignedObjects: Set<number> = new Set();
    public unassignedObjects: Set<number> = new Set();
    public availableObjects: Map<number, string> = new Map();

    public managerAvailableRights: Array<{ name: string; label: string; recommend: boolean }> = [];
    public selectedRights: Array<{ name: string; label: string; recommend: boolean }> = [];

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            manager_id: observable,
            manager_firstname: observable,
            manager_lastname: observable,
            manager_mail: observable,
            manager_assigned_objects: observable,
            objectDataLoading: observable,
            managerDataLoading: observable,
            assignedObjects: observable,
            unassignedObjects: observable,
            availableObjects: observable,
            managerAvailableRights: observable,
            selectedRights: observable,
            originalData: observable,
            onRightsChange: action,
            fetchManager: action,
            fetchObjects: action,
            onSaveManager: action,
            allAvailableRights: computed,
            updateManagerAssignment: action,
            assignedObjectsArray: computed,
            unassignedObjectsArray: computed,
            lastname: computed,
            firstname: computed,
            id: computed,
            validMail: computed,
            validFirstname: computed,
            validLastname: computed,
            isValid: computed,
            hasChanged: computed,
            buildData: computed,
        });

        this.sdk = sdk;
    }

    public async init(config: any): Promise<void> {
        if (config?.manager_id) {
            this.manager_id = config.manager_id;
        }
        await this.fetchManager(config?.manager_id || null);
        // await this.fetchObjects();
    }

    public async fetchManager(id: number): Promise<void> {
        runInAction(() => {
            this.managerDataLoading = true;
        });
        const managerDetails: any = await this.sdk.fetchApi('manager', 'get', { manager_id: id });

        runInAction(() => {
            this.managerDataLoading = false;
            if (managerDetails?.status === 200) {
                const { manager, unassigned_objects, available_permissions } =
                    managerDetails.json || {};

                if (available_permissions) {
                    this.managerAvailableRights = available_permissions;
                }

                if (manager) {
                    this.manager_firstname = manager.firstname;
                    this.manager_lastname = manager.lastname;
                    this.manager_mail = manager.email;
                    this.selectedRights = (manager.permissions || []).map((right: string) => {
                        const rightData = this.managerAvailableRights.find(
                            (r: any) => r.name === right
                        );
                        return {
                            name: right,
                            label: rightData?.label || right,
                            recommend: rightData?.recommend || false,
                        };
                    });
                    manager.assigned_objects.forEach((object: any) => {
                        if (!this.availableObjects.has(object.id)) {
                            this.availableObjects.set(object.id, object.name);
                        }
                        this.assignedObjects.add(object.id);
                    });
                } else {
                    // add default values for a new employee
                    this.selectedRights = this.managerAvailableRights
                        .filter((right: any) => right.recommend)
                        .map((right: any) => ({
                            name: right.name,
                            label: right.label,
                            recommend: right.recommend,
                        }));
                }

                if (unassigned_objects) {
                    unassigned_objects.forEach((object: any) => {
                        if (!this.availableObjects.has(object.id)) {
                            this.availableObjects.set(object.id, object.name);
                        }
                        this.unassignedObjects.add(object.id);
                    });
                }

                this.originalData = this.buildData;
            }
        });
    }

    public async fetchObjects(): Promise<void> {
        // Reset
        runInAction(() => {
            this.objectDataLoading = true;
            this.assignedObjects = new Set();
            this.unassignedObjects = new Set();
        });

        const objectList: any = await this.sdk.fetchApi('object', 'query', {
            format: 'simple',
            limit: 999,
        });

        runInAction(() => {
            this.objectDataLoading = false;

            if (objectList?.status === 200) {
                (objectList.json.data as Array<any>).forEach((object: any) => {
                    if (!this.availableObjects.has(object.id)) {
                        this.availableObjects.set(object.id, object.name);
                    }

                    // add this object to unassigned if it is not already assigned
                    if (this.manager_assigned_objects.has(object.id)) {
                        this.assignedObjects.add(object.id);
                    } else {
                        this.unassignedObjects.add(object.id);
                    }
                });
            }
        });
    }

    public async onSaveManager(): Promise<void> {
        runInAction(() => {
            this.managerDataLoading = true;
        });
        const data = {
            firstname: this.firstname,
            lastname: this.lastname,
            email: this.mail,
            assigned_objects: Array.from(this.assignedObjects).map((id: number) => id),
            permissions: this.selectedRights
                .sort((a: any, b: any) => a.name.localeCompare(b.name))
                .map((right: any) => right.name),
            manager_id: this.manager_id || undefined,
        };

        const managerRequest: any = await this.sdk.fetchApi(
            'manager',
            this.manager_id ? 'update' : 'add',
            data,
            {
                timeout: 20000,
                spinner: true,
                multiPart: false,
            }
        );

        runInAction(() => {
            this.managerDataLoading = false;
        });

        if (managerRequest?.status !== 201 && managerRequest?.status !== 200) {
            const errorCode = managerRequest?.json?.error || '';

            if (errorCode) {
                switch (errorCode) {
                    case 'ERR_VALIDATE_FIRSTNAME':
                        throw new Error('Bitte einen gültigen Vornamen angeben!');
                    case 'ERR_VALIDATE_LASTNAME':
                        throw new Error('Bitte einen gültigen Nachnamen angeben!');
                    case 'ERR_VALIDATE_EMAIL':
                        throw new Error('Bitte gebe eine gültige E-Mail Adresse an!');
                    case 'ERR_DUPLICATED_MAIL':
                        throw new Error('Diese E-Mail Adresse wird bereits verwendet!');
                    case 'ERR_MANAGER_NOT_ADDED':
                        throw new Error(
                            `Fehler beim Erstellen: ${managerRequest?.json?.message || '-'}!`
                        );
                    default:
                        break;
                }
            }

            throw new Error('Ein unbekannter Fehler ist aufgetreten');
        }
    }

    public onRightsChange(rights: any): void {
        this.selectedRights = rights;
    }

    public updateManagerAssignment(
        unassigned: Array<Object_Simple>,
        assigned: Array<Object_Simple>
    ): void {
        this.assignedObjects = new Set(assigned.map(obj => obj.id));
        this.unassignedObjects = new Set(unassigned.map(obj => obj.id));
    }

    get assignedObjectsArray(): Array<Object_Simple> {
        return Array.from(this.assignedObjects).map((id: number) => {
            return {
                id: id,
                name: this.availableObjects.get(id) || '?',
            };
        });
    }

    get unassignedObjectsArray(): Array<Object_Simple> {
        return Array.from(this.unassignedObjects).map((id: number) => {
            return {
                id: id,
                name: this.availableObjects.get(id) || '?',
            };
        });
    }

    set firstname(name: string) {
        // limit to 40 characters
        if (name.length <= 40) {
            runInAction(() => {
                this.manager_firstname = name;
            });
        }
    }

    get firstname(): string {
        return this.manager_firstname;
    }

    set lastname(name: string) {
        // limit to 40 characters
        if (name.length <= 40) {
            runInAction(() => {
                this.manager_lastname = name;
            });
        }
    }

    get lastname(): string {
        return this.manager_lastname;
    }

    set mail(email: string) {
        // limit to 40 characters
        if (email.length <= 40) {
            runInAction(() => {
                this.manager_mail = email;
            });
        }
    }

    get allAvailableRights(): Array<any> {
        return this.managerAvailableRights;
    }

    get mail(): string {
        return this.manager_mail;
    }

    get saveButton(): string {
        return this.manager_id ? 'Aktualisieren' : 'Erstellen';
    }

    get header(): string {
        return this.manager_id ? 'Objektleiter bearbeiten' : 'Neuen Objektleiter anlegen';
    }

    get id(): string {
        return 'manager';
    }

    get validFirstname(): boolean {
        return this.manager_firstname.length >= 3;
    }

    get validLastname(): boolean {
        return this.manager_lastname.length >= 3;
    }

    get validMail(): boolean {
        const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
        return emailRegex.test(this.manager_mail);
    }

    get isValid(): boolean {
        return this.hasChanged && this.validFirstname && this.validLastname && this.validMail;
    }

    get buildData(): any {
        return {
            firstname: this.manager_firstname,
            lastname: this.manager_lastname,
            mail: this.manager_mail,
            rights: this.selectedRights.map((right: any) => right.name),
            objects: Array.from(this.assignedObjects.values()).map((obj: any) => {
                return {
                    id: obj.id,
                };
            }),
        };
    }

    get hasChanged(): boolean {
        if (!this.originalData) return false;
        // Vergleiche den aktuellen Zustand mit dem ursprünglichen Zustand
        const currentData = JSON.parse(JSON.stringify(this.buildData));
        return JSON.stringify(currentData) !== JSON.stringify(this.originalData);
    }
}

export default ManagerDialogStore;
